import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import io from 'socket.io-client';

import './style.css'
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import * as AiIcons from 'react-icons/ai'
import * as MdIcons from 'react-icons/md'
import * as FaIcons from 'react-icons/fa'
import { testeImpressao, getCurrentAccessToken } from "../../services/api";
import getApiUrl from '../../utils/getApiUrl';
import regionConfig from '../../config/regionConfig';

const PageContainer = styled.div`
    display: flex;
    margin-left: ${({ $sidebar }) => ($sidebar ? '250px' : '100px')};
    min-height: 100vh;
    transition: margin-left 200ms ease;
    background: #ffffff;

    @media (max-width: 880px) {
        margin-left: 0;
    }
`;

const ContentWrapper = styled.div`
    width: 100%;
    padding: 24px;

    @media (max-width: 768px) {
        padding: 16px;
    }
`;

const ConfigPrinter = () => {
    const { t } = useTranslation('printer');
    const { sidebar } = useContext(SidebarContext);
    const [currentStep, setCurrentStep] = useState(1);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isTesting, setIsTesting] = useState(false);

    const printSocketRef = useRef(null);
    const printJobIdRef = useRef(null);
    const printTimeoutRef = useRef(null);

    const empresa = localStorage.getItem('empresa')
    const empresaParse = JSON.parse(empresa)
    const objIdEmpresa = empresaParse._id;

    const disconnectPrintSocket = useCallback(() => {
        if (printSocketRef.current) {
            printSocketRef.current.off('print_job_result');
            printSocketRef.current.disconnect();
            printSocketRef.current = null;
        }
        printJobIdRef.current = null;
        if (printTimeoutRef.current) {
            clearTimeout(printTimeoutRef.current);
            printTimeoutRef.current = null;
        }
    }, []);

    const connectPrintSocket = useCallback(() => {
        if (printSocketRef.current) return;

        const API_URL = getApiUrl();
        const userEmpresa = JSON.parse(localStorage.getItem('empresa') || '{}');
        const idEmpresa = userEmpresa._id;

        if (!idEmpresa) return;

        const socket = io(API_URL, {
            withCredentials: true,
            transports: ['websocket'],
            auth: { token: getCurrentAccessToken() },
            reconnection: false,
        });

        socket.on('connect', () => {
            socket.emit('joinCompanyRoom', {
                companyId: idEmpresa,
                clientId: `print_test_${Date.now()}`
            });
        });

        socket.on('print_job_result', (data) => {
            if (data.printJobId === printJobIdRef.current) {
                clearTimeout(printTimeoutRef.current);
                setIsTesting(false);

                if (data.success) {
                    toast.success(t('toasts.testPrintSuccess'));
                    setCurrentStep(3);
                } else {
                    toast.error(t('toasts.testPrintError', { error: data.error || '' }));
                }

                disconnectPrintSocket();
            }
        });

        printSocketRef.current = socket;
    }, [disconnectPrintSocket, t]);

    useEffect(() => {
        return () => {
            disconnectPrintSocket();
        };
    }, [disconnectPrintSocket]);

    const downloadInstallationPackage = async () => {
        setIsDownloading(true);
        try {
            const releasesUrl = `https://api.github.com/repos/${regionConfig.brand.printGithubRepo}/releases/latest`;

            const response = await fetch(releasesUrl);
            if (!response.ok) {
                throw new Error(t('errors.downloadFailed'));
            }
            const data = await response.json();

            const setupFile = regionConfig.brand.printSetupFile;
            const setupAsset = data.assets.find(asset => asset.name === setupFile);
            if (!setupAsset) {
                throw new Error(t('errors.fileNotFound'));
            }

            const downloadUrl = setupAsset.browser_download_url;

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = setupFile;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success(t('toasts.downloadStarted'));
            setCurrentStep(2);
        } catch (error) {
            console.error('Error downloading:', error);
            toast.error(t('errors.downloadError'));
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePrinterTest = async () => {
        if (isTesting) return;
        setIsTesting(true);

        connectPrintSocket();

        try {
            const response = await testeImpressao(objIdEmpresa);
            const printJobId = response.data?.printJobId;

            if (printJobId) {
                printJobIdRef.current = printJobId;
                // Timeout de 30s caso Electron não responda
                printTimeoutRef.current = setTimeout(() => {
                    setIsTesting(false);
                    disconnectPrintSocket();
                    toast.warn(t('toasts.testPrintTimeout'));
                }, 30000);
            } else {
                // Fallback: backend antigo sem printJobId
                setIsTesting(false);
                disconnectPrintSocket();
                if (response.data?.message) {
                    toast.success(t('toasts.testSuccess'));
                    setCurrentStep(3);
                } else {
                    toast.error(t('toasts.testFailed'));
                }
            }
        } catch (error) {
            console.error('Error testing printer:', error);
            setIsTesting(false);
            disconnectPrintSocket();
            toast.error(error.response?.data?.message || t('toasts.testFailed'));
        }
    }

    const handleAlreadyDownloaded = () => {
        setCurrentStep(2);
    }

    return (
        <PermissionGate permissions={['default']}>
            <PageContainer $sidebar={sidebar}>
                <ContentWrapper>
                    {/* Header */}
                    <div className="page-title-with-subtitle">
                        <div className="page-title-with-icon">
                            <div className="page-icon"><MdIcons.MdPrint /></div>
                            <h1 className="page-title">{t('page.title')}</h1>
                        </div>
                        <p className="page-subtitle">{t('page.subtitle', 'Configure a impressora para imprimir comandas automaticamente')}</p>
                    </div>

                        {/* Info Banner */}
                        <div className="info-banner">
                            <div className="info-content">
                                <AiIcons.AiOutlineInfoCircle className="info-icon" />
                                <div>
                                    <h3>{t('infoBanner.title')}</h3>
                                    <p>{t('infoBanner.description')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Steps */}
                        <div className="steps-container">
                            {/* Step 1: Download */}
                            <div className={`step-card ${currentStep >= 1 ? 'active' : ''}`}>
                                <div className={`step-number ${currentStep > 1 ? 'completed' : ''}`}>
                                    {currentStep > 1 ? <FaIcons.FaCheck /> : '1'}
                                </div>
                                <div className="step-header">
                                    <h2>{t('steps.download.title')}</h2>
                                </div>
                                <div className="step-content">
                                    <p>{t('steps.download.description')}</p>
                                    <div className="step-actions">
                                        <button
                                            onClick={downloadInstallationPackage}
                                            className="download-button"
                                            type="button"
                                            disabled={isDownloading}
                                        >
                                            {isDownloading ? (
                                                <AiIcons.AiOutlineLoading3Quarters className="button-icon spin" />
                                            ) : (
                                                <AiIcons.AiOutlineDownload className="button-icon" />
                                            )}
                                            {t('steps.download.buttonDownload')}
                                        </button>
                                        <button
                                            onClick={handleAlreadyDownloaded}
                                            className="already-downloaded-button"
                                            type="button"
                                        >
                                            <AiIcons.AiOutlineCheckCircle className="button-icon" />
                                            {t('steps.download.buttonAlreadyDownloaded')}
                                        </button>
                                    </div>
                                    <div className="step-info">
                                        <small>
                                            <AiIcons.AiOutlineWindows className="os-icon" />
                                            {t('steps.download.compatibility')}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Step 2: Install & Configure */}
                            <div className={`step-card ${currentStep >= 2 ? 'active' : 'disabled'}`}>
                                <div className={`step-number ${currentStep > 2 ? 'completed' : ''}`}>
                                    {currentStep > 2 ? <FaIcons.FaCheck /> : '2'}
                                </div>
                                <div className="step-header">
                                    <h2>{t('steps.install.title')}</h2>
                                </div>
                                <div className="step-content">
                                    <p>{t('steps.install.description')}</p>
                                    <div className="installation-tips">
                                        <h4>{t('steps.install.tipsTitle')}</h4>
                                        <ul>
                                            <li>{t('steps.install.tips.connected')}</li>
                                            <li>{t('steps.install.tips.admin')}</li>
                                            <li>{t('steps.install.tips.keepOpen')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Step 3: Test */}
                            <div className={`step-card ${currentStep >= 2 ? 'active' : 'disabled'}`}>
                                <div className={`step-number ${currentStep > 2 ? 'completed' : ''}`}>
                                    {currentStep > 2 ? <FaIcons.FaCheck /> : '3'}
                                </div>
                                <div className="step-header">
                                    <h2>{t('steps.test.title')}</h2>
                                </div>
                                <div className="step-content">
                                    <p>{t('steps.test.description')}</p>
                                    <div className="step-actions">
                                        <button
                                            type="button"
                                            onClick={handlePrinterTest}
                                            className="test-button"
                                            disabled={currentStep < 2 || isTesting}
                                        >
                                            {isTesting ? (
                                                <AiIcons.AiOutlineLoading3Quarters className="button-icon spin" />
                                            ) : (
                                                <MdIcons.MdPrint className="button-icon" />
                                            )}
                                            {isTesting ? t('toasts.testPrinting') : t('steps.test.buttonTest')}
                                        </button>
                                    </div>
                                    <div className="step-info">
                                        <small>
                                            <AiIcons.AiOutlineCheckCircle className="check-icon" />
                                            {t('steps.test.info')}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Success Card */}
                            {currentStep >= 3 && (
                                <div className="success-card">
                                    <div className="success-content">
                                        <FaIcons.FaCheckCircle className="success-icon" />
                                        <h2>{t('success.title')}</h2>
                                        <p>{t('success.description')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                </ContentWrapper>
            </PageContainer>
        </PermissionGate>
    );
}

export default ConfigPrinter;
